import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import * as FiIcons from 'react-icons/fi';
import { DEPLOYED_APPS } from '../../config/deployedApps';
import { currentSession, checkout, SessionUser, CheckoutItem, Receipt } from '../../config/previewAccess';
import {
  LandingShell, Narrow, Container, Panel, Eyebrow, H2, Sub, Field, ErrorText,
  PrimaryButton, PrimaryLink, GhostLink, T,
} from './ui';

const Ico = (n: keyof typeof FiIcons) => FiIcons[n] as React.ComponentType<any>;
const FiArrowRight = Ico('FiArrowRight');
const FiLock = Ico('FiLock');
const FiCheck = Ico('FiCheck');
const FiCheckCircle = Ico('FiCheckCircle');
const FiCreditCard = Ico('FiCreditCard');
const FiShield = Ico('FiShield');

// Monthly plan price per product (demo).
const PRICES: Record<string, number> = { condo: 49, pestpulse: 59, ehs: 39, nexus: 79, eduwiz: 29 };
const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

interface Plan { id: string; name: string; tagline: string; accent: string; price: number; }
const PLANS: Plan[] = DEPLOYED_APPS.map(a => ({ id: a.id, name: a.name, tagline: a.tagline, accent: a.accent, price: PRICES[a.id] ?? 49 }));

/**
 * Pay for services (ServiceBuds). Requires an account: with no approved preview
 * session we render an account gate; signed-in users pick plans, "pay" (DEMO — no
 * real charge; recorded server-side), and get a receipt.
 */
const Payments: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionUser | null>(null);

  useEffect(() => {
    let alive = true;
    currentSession().then(s => { if (alive) { setSession(s); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  if (loading) return <LandingShell><Container><Loading>Loading…</Loading></Container></LandingShell>;

  // ── Account gate ─────────────────────────────────────────────────────────────
  if (!session) {
    return (
      <LandingShell>
        <Narrow>
          <Panel style={{ textAlign: 'center', marginTop: 24 }}>
            <GateIcon><FiLock /></GateIcon>
            <H2>Create an account to pay</H2>
            <Sub>You’ll need a ServiceBuds account to purchase services. Sign in, or request access — it only takes a minute.</Sub>
            <GateRow>
              <PrimaryLink to="/signup">Request access <FiArrowRight /></PrimaryLink>
              <GhostLink to="/signin">Sign in</GhostLink>
            </GateRow>
          </Panel>
        </Narrow>
      </LandingShell>
    );
  }

  return <Checkout session={session} />;
};

const Checkout: React.FC<{ session: SessionUser }> = ({ session }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cardName, setCardName] = useState(session.name || '');
  const [cardNum, setCardNum] = useState('');
  const [exp, setExp] = useState('');
  const [cvc, setCvc] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);

  const chosen = useMemo(() => PLANS.filter(p => selected.has(p.id)), [selected]);
  const total = chosen.reduce((s, p) => s + p.price, 0);
  const toggle = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const pay = async () => {
    setProcessing(true); setError(null);
    try {
      const items: CheckoutItem[] = chosen.map(p => ({ id: p.id, name: p.name, price: p.price }));
      setReceipt(await checkout(items, total));
    } catch (e: any) {
      setError(e?.message || 'Payment could not be completed.');
    } finally { setProcessing(false); }
  };

  if (receipt) {
    return (
      <LandingShell>
        <Narrow>
          <Panel style={{ textAlign: 'center', marginTop: 24 }}>
            <SuccessIcon><FiCheckCircle /></SuccessIcon>
            <H2>Payment successful</H2>
            <Sub>A receipt for <b style={{ color: T.accent }}>{money(receipt.amount)}</b> has been recorded to {session.email}.</Sub>
            <ReceiptBox>
              <RRow><span>Receipt</span><b>{receipt.id}</b></RRow>
              {receipt.items.map(it => <RRow key={it.id}><span>{it.name}</span><b>{money(it.price)}</b></RRow>)}
              <RTotal><span>Total paid</span><b>{money(receipt.amount)}</b></RTotal>
            </ReceiptBox>
            <GateRow>
              <PrimaryLink to="/my-apps">Go to my apps <FiArrowRight /></PrimaryLink>
              <GhostLink to="/products">Back to products</GhostLink>
            </GateRow>
          </Panel>
        </Narrow>
      </LandingShell>
    );
  }

  return (
    <LandingShell>
      <Container>
        <Head>
          <Eyebrow>Pay for services</Eyebrow>
          <H2>Choose your services</H2>
          <Sub>Signed in as <b>{session.email}</b>. Select the products you’d like, then check out.</Sub>
        </Head>

        <Layout>
          <Plans>
            {PLANS.map(p => {
              const on = selected.has(p.id);
              return (
                <PlanCard key={p.id} type="button" $on={on} $accent={p.accent} onClick={() => toggle(p.id)}>
                  <Checkbox $on={on} $accent={p.accent}>{on && <FiCheck />}</Checkbox>
                  <div style={{ flex: 1 }}>
                    <PlanName>{p.name}</PlanName>
                    <PlanTag>{p.tagline}</PlanTag>
                  </div>
                  <Price>{money(p.price)}<small>/mo</small></Price>
                </PlanCard>
              );
            })}
          </Plans>

          <Aside>
            <Panel>
              <SummaryHead><FiCreditCard /> Order summary</SummaryHead>
              {chosen.length === 0 ? (
                <Muted>No services selected yet.</Muted>
              ) : (
                <>
                  {chosen.map(p => <SumRow key={p.id}><span>{p.name}</span><b>{money(p.price)}</b></SumRow>)}
                  <TotalRow><span>Total</span><b>{money(total)}</b></TotalRow>
                </>
              )}
              <CardForm>
                <Field><span className="lbl">Cardholder name</span><input value={cardName} onChange={e => setCardName(e.target.value)} placeholder="Jane Doe" /></Field>
                <Field><span className="lbl">Card number</span><input value={cardNum} onChange={e => setCardNum(e.target.value.replace(/[^\d ]/g, '').slice(0, 19))} placeholder="4242 4242 4242 4242" /></Field>
                <TwoUp>
                  <Field><span className="lbl">Expiry</span><input value={exp} onChange={e => setExp(e.target.value.slice(0, 5))} placeholder="MM/YY" /></Field>
                  <Field><span className="lbl">CVC</span><input value={cvc} onChange={e => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="123" /></Field>
                </TwoUp>
                <DemoNote><FiShield /> Demo — no card is charged or stored. Use any values.</DemoNote>
                {error && <ErrorText role="alert">{error}</ErrorText>}
                <PrimaryButton type="button" onClick={pay} disabled={processing || chosen.length === 0 || !cardName || !cardNum} style={{ width: '100%' }}>
                  {processing ? 'Processing…' : <>Pay {money(total)}</>}
                </PrimaryButton>
              </CardForm>
            </Panel>
          </Aside>
        </Layout>
      </Container>
    </LandingShell>
  );
};

export default Payments;

// ── styled ────────────────────────────────────────────────────────────────────
const Loading = styled.div`padding:60px; text-align:center; color:${T.text2}; font-weight:600;`;
const GateIcon = styled.div`width:60px; height:60px; margin:0 auto 10px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:26px; color:#fff; background:linear-gradient(135deg,${T.accent},${T.accentDark});`;
const GateRow = styled.div`display:flex; gap:12px; justify-content:center; margin-top:20px; flex-wrap:wrap;`;

const Head = styled.div`margin:16px 0 22px; max-width:640px;`;
const Layout = styled.div`display:grid; grid-template-columns:1.3fr .9fr; gap:22px; align-items:start; @media (max-width:820px){ grid-template-columns:1fr; }`;
const Plans = styled.div`display:flex; flex-direction:column; gap:12px;`;
interface OnAccent { $on: boolean; $accent: string; }
const PlanCard = styled.button<OnAccent>`display:flex; align-items:center; gap:14px; text-align:left; cursor:pointer; font-family:inherit; padding:18px; border-radius:16px;
  background:${p => p.$on ? '#F1F8F6' : '#fff'}; border:1.5px solid ${p => p.$on ? p.$accent : T.border}; transition:all .14s ease;
  &:hover{ border-color:${p => p.$accent}; }`;
const Checkbox = styled.span<OnAccent>`width:26px; height:26px; flex-shrink:0; border-radius:8px; display:flex; align-items:center; justify-content:center; color:#fff; font-size:15px;
  background:${p => p.$on ? p.$accent : '#fff'}; border:1.5px solid ${p => p.$on ? p.$accent : '#cbd5d3'};`;
const PlanName = styled.div`font-size:15.5px; font-weight:800; color:${T.text};`;
const PlanTag = styled.div`font-size:12.5px; color:${T.muted};`;
const Price = styled.div`font-size:20px; font-weight:800; color:${T.text}; small{ font-size:12px; font-weight:600; color:${T.muted}; }`;

const Aside = styled.div`position:sticky; top:20px;`;
const SummaryHead = styled.div`display:flex; align-items:center; gap:8px; font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:.05em; color:${T.muted}; margin-bottom:14px; svg{ color:${T.accent}; }`;
const Muted = styled.div`font-size:13.5px; color:${T.muted}; padding:6px 0 2px;`;
const SumRow = styled.div`display:flex; justify-content:space-between; font-size:14px; padding:6px 0; color:${T.text2}; b{ color:${T.text}; }`;
const TotalRow = styled.div`display:flex; justify-content:space-between; align-items:center; border-top:1px solid ${T.border}; margin-top:8px; padding-top:12px; font-size:14px; font-weight:700; color:${T.text}; b{ font-size:18px; color:${T.accent}; }`;
const CardForm = styled.div`display:flex; flex-direction:column; gap:12px; margin-top:18px; border-top:1px dashed ${T.border}; padding-top:18px;`;
const TwoUp = styled.div`display:grid; grid-template-columns:1fr 1fr; gap:12px;`;
const DemoNote = styled.p`display:flex; align-items:center; gap:6px; font-size:11.5px; color:${T.muted}; margin:0; svg{ font-size:13px; }`;

const SuccessIcon = styled.div`width:64px; height:64px; margin:0 auto 10px; border-radius:18px; display:flex; align-items:center; justify-content:center; font-size:30px; color:#fff; background:linear-gradient(135deg,${T.ok},${T.accentDark});`;
const ReceiptBox = styled.div`max-width:420px; margin:18px auto 0; text-align:left; background:${T.field}; border:1px solid ${T.border}; border-radius:14px; padding:16px 18px;`;
const RRow = styled.div`display:flex; justify-content:space-between; font-size:13.5px; padding:4px 0; color:${T.text2}; b{ color:${T.text}; word-break:break-all; }`;
const RTotal = styled.div`display:flex; justify-content:space-between; border-top:1px solid ${T.border}; margin-top:8px; padding-top:10px; font-size:14px; font-weight:700; b{ color:${T.accent}; }`;
